// pages/Register/CheckoutPage.tsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Check,
    ChevronLeft,
    CreditCard,
    Shield,
    Lock,
    Zap,
    Users,
    Calendar,
    ArrowRight,
    AlertCircle
} from 'lucide-react';

interface Plan {
    id: 'starter' | 'professional' | 'enterprise';
    name: string;
    description: string;
    price: number;
    priceAnnual: number;
    features: string[];
    popular?: boolean;
    maxAgents: number;
    icon: React.ReactNode;
}

const CheckoutPage = () => {
    const navigate = useNavigate();
    const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly');
    const [selectedPlan, setSelectedPlan] = useState<'starter' | 'professional' | 'enterprise'>('professional');
    const [isProcessing, setIsProcessing] = useState(false);
    const [cardError, setCardError] = useState('');

    const plans: Plan[] = [
        {
            id: 'starter',
            name: 'Starter',
            description: 'Pour bien démarrer',
            price: 29,
            priceAnnual: 23,
            maxAgents: 5,
            icon: <Zap className="h-6 w-6" />,
            features: [
                'Appels entrants/sortants',
                'IVR de base',
                'Boîte vocale',
                'Enregistrement des appels',
                '1 numéro local inclus',
                'Rapports de base',
                'Support par email'
            ]
        },
        {
            id: 'professional',
            name: 'Professional',
            description: 'Le plus populaire',
            price: 59,
            priceAnnual: 47,
            maxAgents: 50,
            popular: true,
            icon: <Users className="h-6 w-6" />,
            features: [
                'Tout dans Starter',
                'IVR avancé',
                'Files d\'attente intelligentes',
                'WhatsApp Business',
                'SMS illimités',
                'Intégration CRM',
                'Rapports avancés',
                'Support prioritaire 24/7'
            ]
        },
        {
            id: 'enterprise',
            name: 'Enterprise',
            description: 'Pour les grandes structures',
            price: 99,
            priceAnnual: 79,
            maxAgents: 200,
            icon: <Shield className="h-6 w-6" />,
            features: [
                'Tout dans Professional',
                'Intégrations avancées (API)',
                'Routage basé sur les compétences',
                'Tableaux de bord personnalisés',
                'IA Gaston (Assistant intelligent)',
                'Sécurité avancée & SSO',
                'Gestion avancée des rôles',
                'Account manager dédié'
            ]
        }
    ];

    const selectedPlanData = plans.find(p => p.id === selectedPlan)!;
    const currentPrice = billingCycle === 'monthly' 
        ? selectedPlanData.price 
        : selectedPlanData.priceAnnual;

    const handlePayment = async () => {
        setIsProcessing(true);
        setCardError('');

        try {
            // Simuler le paiement
            await new Promise(resolve => setTimeout(resolve, 2000));
            
            // Redirection vers le dashboard ou page de confirmation
            navigate('/dashboard');
        } catch (error) {
            setCardError('Une erreur est survenue lors du paiement. Veuillez réessayer.');
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-[#F8FAFF] to-[#EEF2FF] py-6 px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <button
                        onClick={() => navigate(-1)}
                        className="flex items-center gap-2 text-sm text-slate-500 hover:text-[#031B4E] transition-colors"
                    >
                        <ChevronLeft className="h-4 w-4" />
                        Retour
                    </button>
                    <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-slate-400">Étape 2 sur 2</span>
                        <div className="w-32 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                            <div className="h-full w-full bg-blue-600 rounded-full"></div>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* LEFT: Sélection des offres */}
                    <div className="lg:col-span-2 space-y-5">
                        <div className="bg-white rounded-2xl shadow-lg border border-slate-200/60 p-6 sm:p-8">
                            <div className="mb-6">
                                <h1 className="text-2xl font-bold text-[#031B4E]">
                                    Choisissez votre offre
                                </h1>
                                <p className="text-sm text-slate-500">
                                    Sélectionnez le forfait qui correspond à vos besoins
                                </p>
                            </div>

                            {/* Toggle billing */}
                            <div className="flex items-center gap-3 mb-6">
                                <div className="bg-slate-100 p-1 rounded-xl flex">
                                    <button
                                        onClick={() => setBillingCycle('monthly')}
                                        className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all ${
                                            billingCycle === 'monthly'
                                                ? 'bg-[#031B4E] text-white shadow-sm'
                                                : 'text-slate-600 hover:text-slate-900'
                                        }`}
                                    >
                                        Mensuel
                                    </button>
                                    <button
                                        onClick={() => setBillingCycle('annual')}
                                        className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all ${
                                            billingCycle === 'annual'
                                                ? 'bg-[#031B4E] text-white shadow-sm'
                                                : 'text-slate-600 hover:text-slate-900'
                                        }`}
                                    >
                                        Annuel <span className="text-[10px] text-green-500">-20%</span>
                                    </button>
                                </div>
                                <span className="text-[10px] bg-green-100 text-green-700 px-2 py-1 rounded-lg font-bold">
                                    2 mois offerts
                                </span>
                            </div>

                            {/* Plans */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                {plans.map((plan) => (
                                    <button
                                        key={plan.id}
                                        onClick={() => setSelectedPlan(plan.id)}
                                        className={`relative p-4 rounded-2xl border-2 text-left transition-all ${
                                            selectedPlan === plan.id
                                                ? 'border-[#031B4E] bg-[#031B4E]/5 shadow-md'
                                                : 'border-slate-200 hover:border-slate-300 bg-white'
                                        }`}
                                    >
                                        {plan.popular && (
                                            <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-blue-600 text-white text-[10px] font-bold px-3 py-0.5 rounded-full">
                                                Populaire
                                            </div>
                                        )}
                                        
                                        <div className="flex items-center gap-2 mb-2">
                                            <div className={`p-1.5 rounded-lg ${
                                                selectedPlan === plan.id ? 'bg-[#031B4E] text-white' : 'bg-slate-100 text-slate-600'
                                            }`}>
                                                {plan.icon}
                                            </div>
                                            <span className="text-xs font-bold text-slate-600">{plan.maxAgents} agents</span>
                                        </div>

                                        <h3 className="font-bold text-slate-900">{plan.name}</h3>
                                        <p className="text-[10px] text-slate-500">{plan.description}</p>

                                        <div className="mt-2">
                                            <span className="text-xl font-black text-[#031B4E]">
                                                {currentPrice} $
                                            </span>
                                            <span className="text-[10px] text-slate-400 ml-1">
                                                /agent/mois
                                            </span>
                                        </div>

                                        {selectedPlan === plan.id && (
                                            <div className="mt-2 flex items-center gap-1 text-xs font-medium text-blue-600">
                                                <Check className="h-3 w-3" />
                                                Sélectionné
                                            </div>
                                        )}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Features détaillées */}
                        <div className="bg-white rounded-2xl shadow-lg border border-slate-200/60 p-6">
                            <h3 className="text-sm font-bold text-slate-700 mb-4">
                                Ce qui est inclus dans <span className="text-[#031B4E]">{selectedPlanData.name}</span>
                            </h3>
                            <div className="grid grid-cols-2 gap-2">
                                {selectedPlanData.features.map((feature, idx) => (
                                    <div key={idx} className="flex items-center gap-2 text-xs text-slate-600">
                                        <Check className="h-3.5 w-3.5 text-green-500 shrink-0" />
                                        {feature}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* RIGHT: Paiement */}
                    <div className="lg:col-span-1">
                        <div className="bg-white rounded-2xl shadow-lg border border-slate-200/60 p-6 sticky top-6">
                            <h2 className="text-lg font-bold text-[#031B4E] mb-4">
                                Récapitulatif
                            </h2>

                            <div className="space-y-3 mb-6">
                                <div className="flex justify-between text-sm">
                                    <span className="text-slate-500">Forfait</span>
                                    <span className="font-medium">{selectedPlanData.name}</span>
                                </div>
                                <div className="flex justify-between text-sm">
                                    <span className="text-slate-500">Facturation</span>
                                    <span className="font-medium">
                                        {billingCycle === 'monthly' ? 'Mensuelle' : 'Annuelle'}
                                    </span>
                                </div>
                                <div className="flex justify-between text-sm">
                                    <span className="text-slate-500">Prix unitaire</span>
                                    <span className="font-medium">{currentPrice} $ /agent</span>
                                </div>
                                <div className="border-t border-slate-200 pt-3">
                                    <div className="flex justify-between text-base font-bold">
                                        <span>Total</span>
                                        <span className="text-[#031B4E]">{currentPrice} $</span>
                                    </div>
                                    <p className="text-[10px] text-slate-400 text-right">
                                        {billingCycle === 'monthly' ? 'Facturé mensuellement' : 'Facturé annuellement'}
                                    </p>
                                </div>
                            </div>

                            <div className="bg-blue-50 rounded-xl p-3 border border-blue-100 mb-4">
                                <div className="flex items-start gap-2">
                                    <Calendar className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
                                    <div>
                                        <p className="text-xs font-medium text-blue-800">
                                            Période d'essai de 14 jours
                                        </p>
                                        <p className="text-[10px] text-blue-600/70">
                                            Premier paiement à la fin de l'essai
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Carte de paiement (simulée) */}
                            <div className="space-y-3 mb-4">
                                <label className="text-xs font-semibold text-slate-700">
                                    Informations de paiement
                                </label>
                                <div className="border border-slate-200 rounded-xl p-3 bg-slate-50/50">
                                    <div className="flex items-center gap-2">
                                        <CreditCard className="h-5 w-5 text-slate-400" />
                                        <span className="text-xs text-slate-600">•••• •••• •••• 4242</span>
                                        <span className="text-[10px] text-slate-400 ml-auto">Exp. 12/26</span>
                                    </div>
                                </div>

                                {/* CVC */}
                                <div className="grid grid-cols-2 gap-2">
                                    <input
                                        type="text"
                                        placeholder="CVC"
                                        className="px-3 py-2 text-sm border border-slate-200 rounded-lg focus:border-[#031B4E] focus:outline-none focus:ring-2 focus:ring-[#031B4E]/10"
                                        maxLength={4}
                                    />
                                    <input
                                        type="text"
                                        placeholder="Code postal"
                                        className="px-3 py-2 text-sm border border-slate-200 rounded-lg focus:border-[#031B4E] focus:outline-none focus:ring-2 focus:ring-[#031B4E]/10"
                                    />
                                </div>
                            </div>

                            {cardError && (
                                <div className="bg-red-50 rounded-lg p-2 mb-3 flex items-center gap-2">
                                    <AlertCircle className="h-4 w-4 text-red-500" />
                                    <p className="text-xs text-red-600">{cardError}</p>
                                </div>
                            )}

                            <button
                                onClick={handlePayment}
                                disabled={isProcessing}
                                className="w-full bg-[#031B4E] hover:bg-[#052975] text-white font-bold py-3 px-4 rounded-xl transition-all shadow-lg shadow-blue-900/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                                {isProcessing ? (
                                    <>
                                        <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                        <span>Traitement...</span>
                                    </>
                                ) : (
                                    <>
                                        <span>Démarrer l'essai gratuit</span>
                                        <ArrowRight className="h-4 w-4" />
                                    </>
                                )}
                            </button>

                            <div className="flex items-center justify-center gap-4 mt-4 text-[10px] text-slate-400">
                                <span className="flex items-center gap-1">
                                    <Lock className="h-3 w-3" />
                                    Paiement sécurisé
                                </span>
                                <span>•</span>
                                <span>Annulation à tout moment</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CheckoutPage;